// Feedback Service - Supabase integration for feedback system
// Handles all CRUD operations for conversations and messages
// Uses prod schema with tenant scoping for multi-tenancy

import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';
import { getProfilePhotoUrl } from '@/lib/supabase/profile-helpers';
import { isConfirmedFeatureRequest } from '../utils/feedbackBuckets';
import type { MessageActivity } from '../utils/feedbackReadState';
import type {
  FeedbackConversation,
  FeedbackMessage,
  FeedbackCategory,
  FeedbackStats,
  FeedbackListParams,
  FeedbackListResponse,
  ConversationWithMessages,
  CreateFeedbackRequest,
  CreateFeedbackResponse,
  AddMessageRequest,
  UpdateConversationRequest,
  FeedbackUser
} from '../types';

// Helper function to get tenant_id from JWT or user_approvals
const getTenantId = async (): Promise<string | null> => {
  const prodClient = getProdSupabaseClient();
  const {
    data: { session }
  } = await supabase.auth.getSession();

  if (!session?.access_token) return null;

  try {
    // First try to get tenant_id from JWT
    const payload = JSON.parse(atob(session.access_token.split('.')[1]));
    if (payload.tenant_id) {
      return payload.tenant_id;
    }

    // Fallback: get tenant_id from user_approvals if not in JWT
    // This handles cases where user hasn't refreshed their token yet
    const { data: userApproval } = await prodClient
      .from('user_approvals')
      .select('tenant_id')
      .eq('user_id', payload.sub)
      .eq('status', 'approved')
      .maybeSingle();

    if (userApproval?.tenant_id) {
      console.log('[Feedback] Got tenant_id from user_approvals fallback:', userApproval.tenant_id);
      return userApproval.tenant_id;
    }

    return null;
  } catch (error) {
    console.error('[Feedback] Error getting tenant_id:', error);
    return null;
  }
};

// ============================================================================
// CONVERSATION OPERATIONS
// ============================================================================

/**
 * Fetch all feedback conversations with pagination and filtering
 * Now uses prod schema with tenant scoping
 */
export const getConversations = async (
  params: FeedbackListParams = {}
): Promise<FeedbackListResponse> => {
  const {
    page = 1,
    page_size = 25,
    sort_by = 'created_at',
    sort_order = 'desc',
    filters = {}
  } = params;

  const prodClient = getProdSupabaseClient();

  let query = prodClient
    .from('feedback_conversations_with_details')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters.priority && filters.priority.length > 0) {
    query = query.in('priority', filters.priority);
  }

  if (filters.category_id && filters.category_id.length > 0) {
    query = query.in('category_id', filters.category_id);
  }

  if (filters.assigned_to && filters.assigned_to.length > 0) {
    query = query.in('assigned_to', filters.assigned_to);
  }

  if (filters.user_id) {
    query = query.eq('user_id', filters.user_id);
  }

  if (filters.search) {
    query = query.or(`title.ilike.%${filters.search}%`);
  }

  if (filters.date_from) {
    query = query.gte('created_at', filters.date_from);
  }

  if (filters.date_to) {
    query = query.lte('created_at', filters.date_to);
  }

  // Apply sorting
  query = query.order(sort_by, { ascending: sort_order === 'asc' });

  // Apply pagination
  const from = (page - 1) * page_size;
  const to = from + page_size - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching conversations:', error);
    throw new Error(`Failed to fetch conversations: ${error.message}`);
  }

  const total = count || 0;
  const total_pages = Math.ceil(total / page_size);

  return {
    conversations: (data as FeedbackConversation[]) || [],
    total,
    page,
    page_size,
    total_pages,
    has_more: page < total_pages
  };
};

/**
 * Get a single conversation by ID with all messages
 */
export const getConversationById = async (
  conversationId: string,
  userId?: string
): Promise<ConversationWithMessages | null> => {
  const prodClient = getProdSupabaseClient();

  // Fetch conversation details
  const { data: conversation, error: convError } = await prodClient
    .from('feedback_conversations_with_details')
    .select('*')
    .eq('id', conversationId)
    .maybeSingle();

  if (convError) {
    console.error('Error fetching conversation:', convError);
    throw new Error(`Failed to fetch conversation: ${convError.message}`);
  }

  // If conversation doesn't exist (e.g., was deleted), return null
  if (!conversation) {
    return null;
  }

  // Fetch messages
  const { data: messages, error: msgError } = await prodClient
    .from('feedback_messages_with_details')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (msgError) {
    console.error('Error fetching messages:', msgError);
    throw new Error(`Failed to fetch messages: ${msgError.message}`);
  }

  return {
    ...(conversation as FeedbackConversation),
    messages: (messages as FeedbackMessage[]) || []
  };
};

/**
 * Create a new feedback conversation with initial message
 * Now includes tenant_id from JWT context
 */
export const createConversation = async (
  request: CreateFeedbackRequest
): Promise<CreateFeedbackResponse> => {
  const prodClient = getProdSupabaseClient();

  // Get current user
  const {
    data: { user },
    error: userError
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User must be authenticated to create feedback');
  }

  // Get tenant_id from JWT
  const tenantId = await getTenantId();
  if (!tenantId) {
    throw new Error('No tenant context found. Please log in again.');
  }

  // Create the conversation with tenant_id
  const { data: conversation, error: convError } = await prodClient
    .from('feedback_conversations')
    .insert({
      tenant_id: tenantId,
      user_id: user.id,
      title: request.title,
      category_id: request.category_id,
      priority: request.priority || 'medium'
    })
    .select()
    .single();

  if (convError) {
    console.error('Error creating conversation:', convError);
    throw new Error(`Failed to create conversation: ${convError.message}`);
  }

  // Add the initial message with attachments if present
  const { data: message, error: msgError } = await prodClient
    .from('feedback_messages')
    .insert({
      conversation_id: conversation.id,
      sender_id: user.id,
      message: request.initial_message,
      message_type: 'text',
      attachments:
        request.attachments && request.attachments.length > 0 ? request.attachments : null
    })
    .select()
    .single();

  if (msgError) {
    console.error('Error creating initial message:', msgError);
    // Clean up the conversation if message creation fails
    await prodClient.from('feedback_conversations').delete().eq('id', conversation.id);
    throw new Error(`Failed to create initial message: ${msgError.message}`);
  }

  return {
    conversation: conversation as FeedbackConversation,
    message: message as FeedbackMessage
  };
};

/**
 * Update conversation status, priority, or assignment (admin only)
 */
export const updateConversation = async (
  conversationId: string,
  updates: UpdateConversationRequest
): Promise<FeedbackConversation> => {
  const prodClient = getProdSupabaseClient();
  const updateData: Partial<FeedbackConversation> = {};

  if (updates.status) {
    updateData.status = updates.status;
    if (updates.status === 'resolved') {
      updateData.resolved_at = new Date().toISOString();
      updateData.archived_at = null as any;
    } else if (updates.status === 'archived') {
      updateData.archived_at = new Date().toISOString();
    } else if (updates.status === 'open' || updates.status === 'in_progress') {
      updateData.resolved_at = null as any;
      updateData.archived_at = null as any;
    }
  }

  if (updates.priority) {
    updateData.priority = updates.priority;
  }

  if (updates.assigned_to !== undefined) {
    updateData.assigned_to = updates.assigned_to;
  }

  if (updates.category_id) {
    updateData.category_id = updates.category_id;
  }

  const { data, error } = await prodClient
    .from('feedback_conversations')
    .update(updateData)
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.error('Error updating conversation:', error);
    throw new Error(`Failed to update conversation: ${error.message}`);
  }

  return data as FeedbackConversation;
};

// ============================================================================
// MESSAGE OPERATIONS
// ============================================================================

/**
 * Add a message to a conversation
 */
export const addMessage = async (request: AddMessageRequest): Promise<FeedbackMessage> => {
  const prodClient = getProdSupabaseClient();

  // Get current user
  const {
    data: { user },
    error: userError
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User must be authenticated to add messages');
  }

  const { data, error } = await prodClient
    .from('feedback_messages')
    .insert({
      conversation_id: request.conversation_id,
      sender_id: user.id,
      message: request.message,
      message_type: request.message_type || 'text',
      attachments: request.attachments
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding message:', error);
    throw new Error(`Failed to add message: ${error.message}`);
  }

  return data as FeedbackMessage;
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (
  conversationId: string,
  messageIds?: string[]
): Promise<void> => {
  const prodClient = getProdSupabaseClient();

  // Get current user
  const {
    data: { user },
    error: userError
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User must be authenticated to mark messages as read');
  }

  let query = prodClient
    .from('feedback_messages')
    .update({
      read_at: new Date().toISOString(),
      read_by: user.id
    })
    .eq('conversation_id', conversationId)
    .neq('sender_id', user.id); // Don't mark own messages as read

  if (messageIds && messageIds.length > 0) {
    query = query.in('id', messageIds);
  }

  const { error } = await query;

  if (error) {
    console.error('Error marking messages as read:', error);
    throw new Error(`Failed to mark messages as read: ${error.message}`);
  }
};

/**
 * Update a message's text content (admin only)
 */
export const updateMessage = async (
  messageId: string,
  newMessage: string
): Promise<FeedbackMessage> => {
  const prodClient = getProdSupabaseClient();

  const { data, error } = await prodClient
    .from('feedback_messages')
    .update({
      message: newMessage,
      updated_at: new Date().toISOString()
    })
    .eq('id', messageId)
    .select()
    .single();

  if (error) {
    console.error('Error updating message:', error);
    throw new Error(`Failed to update message: ${error.message}`);
  }

  return data as FeedbackMessage;
};

/**
 * Delete a message (admin or message owner)
 */
export const deleteMessage = async (messageId: string): Promise<void> => {
  const prodClient = getProdSupabaseClient();

  const { error } = await prodClient.from('feedback_messages').delete().eq('id', messageId);

  if (error) {
    console.error('Error deleting message:', error);
    throw new Error(`Failed to delete message: ${error.message}`);
  }
};

// ============================================================================
// CATEGORY OPERATIONS
// ============================================================================

/**
 * Get all feedback categories
 * Categories are shared across tenants
 */
export const getCategories = async (): Promise<FeedbackCategory[]> => {
  const prodClient = getProdSupabaseClient();

  const { data, error } = await prodClient
    .from('feedback_categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('Error fetching categories:', error);
    throw new Error(`Failed to fetch categories: ${error.message}`);
  }

  return (data as FeedbackCategory[]) || [];
};

// ============================================================================
// STATISTICS OPERATIONS
// ============================================================================

/**
 * Get who sent each message since `since` (no bodies), for unread highlighting.
 * RLS scopes rows to the caller's tenant (super admins see all).
 */
export const getMessageActivity = async (since: string): Promise<MessageActivity[]> => {
  const prodClient = getProdSupabaseClient();

  const { data, error } = await prodClient
    .from('feedback_messages')
    .select('conversation_id, sender_id, created_at, message_type')
    .gt('created_at', since)
    .order('created_at', { ascending: false })
    .limit(1000);

  if (error) {
    throw new Error(`Failed to fetch message activity: ${error.message}`);
  }

  return (data || []) as MessageActivity[];
};

/**
 * Get feedback statistics for admin dashboard
 * Scoped to current tenant
 */
export const getFeedbackStats = async (): Promise<FeedbackStats> => {
  const prodClient = getProdSupabaseClient();

  // Get basic conversation counts (RLS will filter by tenant)
  const { data: conversations, error: convError } = await prodClient
    .from('feedback_conversations')
    .select('status, priority, category_id, created_at, resolved_at');

  if (convError) {
    console.error('Error fetching conversation stats:', convError);
    throw new Error(`Failed to fetch conversation stats: ${convError.message}`);
  }

  // Get category breakdown
  const { data: categories, error: catError } = await prodClient
    .from('feedback_categories')
    .select('id, name, display_name');

  if (catError) {
    console.error('Error fetching categories:', catError);
    throw new Error(`Failed to fetch categories: ${catError.message}`);
  }

  // Calculate statistics
  const total = conversations?.length || 0;
  const statusCounts = (conversations || []).reduce(
    (acc, conv) => {
      acc[conv.status] = (acc[conv.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Confirmed feature requests are counted on their own, not as In Progress
  const categoryRows = (categories || []) as Pick<FeedbackCategory, 'id' | 'name'>[];
  type StatusRow = Pick<FeedbackConversation, 'status' | 'category_id'>;
  const statusRows = (conversations || []) as StatusRow[];
  const categoryNameById = new Map(categoryRows.map(cat => [cat.id, cat.name]));
  const featureRequestCount = statusRows.filter(conv => {
    const categoryName = conv.category_id ? categoryNameById.get(conv.category_id) : undefined;
    return isConfirmedFeatureRequest({
      status: conv.status,
      ...(categoryName && { category_name: categoryName })
    });
  }).length;

  const priorityCounts = (conversations || []).reduce(
    (acc, conv) => {
      acc[conv.priority] = (acc[conv.priority] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Category breakdown
  const categoryBreakdown = (categories || []).map(cat => {
    const count = (conversations || []).filter(conv => conv.category_id === cat.id).length;
    return {
      category_name: cat.name,
      category_display_name: cat.display_name,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0
    };
  });

  // Time-based calculations
  const today = new Date().toISOString().split('T')[0];
  const conversationsCreatedToday = (conversations || []).filter(conv =>
    conv.created_at?.startsWith(today)
  ).length;

  const conversationsResolvedToday = (conversations || []).filter(
    conv => conv.resolved_at && conv.resolved_at.startsWith(today)
  ).length;

  // Average resolution time (in hours)
  const resolvedConversations = (conversations || []).filter(conv => conv.resolved_at);
  const avgResolutionTime =
    resolvedConversations.length > 0
      ? resolvedConversations.reduce((sum, conv) => {
        const created = new Date(conv.created_at);
        const resolved = new Date(conv.resolved_at!);
        const diffHours = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
        return sum + diffHours;
      }, 0) / resolvedConversations.length
      : 0;

  return {
    total_conversations: total,
    open_conversations: statusCounts.open || 0,
    in_progress_conversations: (statusCounts.in_progress || 0) - featureRequestCount,
    feature_request_conversations: featureRequestCount,
    resolved_conversations: statusCounts.resolved || 0,
    archived_conversations: statusCounts.archived || 0,

    urgent_count: priorityCounts.urgent || 0,
    high_count: priorityCounts.high || 0,
    medium_count: priorityCounts.medium || 0,
    low_count: priorityCounts.low || 0,

    category_breakdown: categoryBreakdown,

    avg_resolution_time_hours: avgResolutionTime,
    conversations_created_today: conversationsCreatedToday,
    conversations_resolved_today: conversationsResolvedToday
  };
};

// ============================================================================
// USER OPERATIONS
// ============================================================================

/**
 * Get users for assignment dropdown
 * Now uses prod schema user_approvals with tenant scoping
 */
export const getUsers = async (): Promise<FeedbackUser[]> => {
  const prodClient = getProdSupabaseClient();

  // Get tenant_id to filter users
  const tenantId = await getTenantId();

  let query = prodClient
    .from('user_approvals')
    .select(
      `
      user_id,
      email,
      full_name
    `
    )
    .not('email', 'is', null);

  // If we have tenant context, filter by tenant
  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching users:', error);
    throw new Error(`Failed to fetch users: ${error.message}`);
  }

  return (data as any[]).map(user => ({
    user_id: user.user_id,
    email: user.email,
    full_name: user.full_name,
    display_name: user.full_name || user.email?.split('@')[0] || 'Unknown'
  }));
};

/**
 * Batch-fetch profile photo URLs for a list of user IDs
 */
export const getSenderProfilePhotos = async (
  senderIds: string[]
): Promise<Record<string, string>> => {
  if (senderIds.length === 0) return {};

  const prodClient = getProdSupabaseClient();
  const { data, error } = await prodClient
    .from('user_profiles')
    .select('user_id, profile_photo_path')
    .in('user_id', senderIds);

  if (error || !data) return {};

  const photoMap: Record<string, string> = {};
  for (const row of data as { user_id: string; profile_photo_path: string | null }[]) {
    const url = getProfilePhotoUrl(row.profile_photo_path);
    if (url) photoMap[row.user_id] = url;
  }
  return photoMap;
};

// ============================================================================
// EMAIL NOTIFICATION OPERATIONS
// ============================================================================

/**
 * Get notification log for admin dashboard
 */
export const getNotificationLog = async (
  params: {
    page?: number;
    page_size?: number;
    status?: string[];
    date_from?: string;
    date_to?: string;
  } = {}
): Promise<{
  notifications: any[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}> => {
  const prodClient = getProdSupabaseClient();
  const { page = 1, page_size = 25, status = [], date_from, date_to } = params;

  let query = prodClient.from('feedback_notifications_log').select('*', { count: 'exact' });

  // Apply filters
  if (status.length > 0) {
    query = query.in('status', status);
  }

  if (date_from) {
    query = query.gte('sent_at', date_from);
  }

  if (date_to) {
    query = query.lte('sent_at', date_to);
  }

  // Apply sorting and pagination
  query = query.order('sent_at', { ascending: false });

  const from = (page - 1) * page_size;
  const to = from + page_size - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching notification log:', error);
    throw new Error(`Failed to fetch notification log: ${error.message}`);
  }

  const total = count || 0;
  const total_pages = Math.ceil(total / page_size);

  return {
    notifications: data || [],
    total,
    page,
    page_size,
    total_pages
  };
};

/**
 * Test email notification (for admin testing)
 */
export const testEmailNotification = async (): Promise<void> => {
  const { error } = await supabase.functions.invoke('feedback-notifications', {
    body: {
      conversation: {
        id: 'test-conversation-id',
        user_id: 'test-user-id',
        title: 'Test Feedback',
        description: 'This is a test feedback for email notification',
        category_id: 'general',
        status: 'open',
        priority: 'medium',
        created_at: new Date().toISOString(),
        user_email: 'test@example.com',
        category_name: 'general',
        category_display_name: 'General Feedback'
      },
      message: {
        id: 'test-message-id',
        conversation_id: 'test-conversation-id',
        sender_id: 'test-user-id',
        message: 'This is a test message to verify email notifications are working correctly.',
        created_at: new Date().toISOString(),
        sender_email: 'test@example.com'
      }
    }
  });

  if (error) {
    console.error('Error testing email notification:', error);
    throw new Error(`Failed to test email notification: ${error.message}`);
  }
};

/**
 * Close/Resolve feedback by setting status to 'resolved'
 */
export const closeFeedback = async (conversationId: string): Promise<void> => {
  const prodClient = getProdSupabaseClient();

  const { error } = await prodClient
    .from('feedback_conversations')
    .update({
      status: 'resolved',
      resolved_at: new Date().toISOString()
    })
    .eq('id', conversationId);

  if (error) {
    console.error('Error closing feedback:', error);
    throw new Error(`Failed to close feedback: ${error.message}`);
  }
};

/**
 * Delete feedback conversation and all related messages
 * Messages are deleted via CASCADE constraint
 */
export const deleteFeedback = async (conversationId: string): Promise<void> => {
  const prodClient = getProdSupabaseClient();

  // Delete the conversation (messages will be deleted via CASCADE)
  const { error: conversationError } = await prodClient
    .from('feedback_conversations')
    .delete()
    .eq('id', conversationId);

  if (conversationError) {
    console.error('Error deleting feedback conversation:', conversationError);
    throw new Error(`Failed to delete feedback conversation: ${conversationError.message}`);
  }
};

// ============================================================================
// EXPORT ALL FUNCTIONS
// ============================================================================

export const feedbackService = {
  // Conversations
  getConversations,
  getConversationById,
  createConversation,
  updateConversation,
  closeFeedback,
  deleteFeedback,

  // Messages
  addMessage,
  updateMessage,
  deleteMessage,
  markMessagesAsRead,
  getMessageActivity,

  // Categories
  getCategories,

  // Statistics
  getFeedbackStats,

  // Users
  getUsers,

  // Email Notifications
  getNotificationLog,
  testEmailNotification
};
